/**
 * generativeUi.js
 *
 * Pure, framework-free helpers for the operator orb's generative-UI renderers.
 * Kept in plain ESM (JSDoc-typed) so node --test can exercise the render
 * CONTRACT directly, without a browser or a build step: the renderer maps these
 * specs to JSX 1:1, so a test that pins the spec pins the pixels.
 *
 * Two contracts guarded here, both regressions we shipped and then caught:
 *   1. donutSpec  -> the trust_score_donut is a RING, never a filled pie. The
 *      hole is a genuinely empty SVG centre (background shows through), not an
 *      opaque disc laid over a conic-gradient. Every arc is stroke-only
 *      (fill:'none'); a filled/pie render violates the contract.
 *   2. chipDisplay -> one fact per chip. A tile label that leads with its own
 *      value ("16 resolve dispatches", value "16") must not render the number
 *      twice; the value is the figure, the label is the descriptor.
 */

/**
 * Geometry + structure for a real donut ring at `pct` percent (0..100).
 * The renderer draws exactly the circles this returns, so the ring-ness is
 * testable without a DOM. `isRing` and every circle's `fill:'none'` are the
 * anti-pie contract; `holeRadius > 0` proves the centre is actually empty.
 *
 * @param {number} pct
 * @returns {{
 *   size: number, center: number, viewBox: string, radius: number,
 *   stroke: number, holeRadius: number, circumference: number, isRing: true,
 *   pct: number,
 *   circles: Array<{ role: 'track'|'progress', fill: 'none', stroke: string,
 *     dashArray?: string }>
 * }}
 */
export function donutSpec(pct) {
  const size = 76;
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, Number.isFinite(+pct) ? +pct : 0));
  const dash = (clamped / 100) * circumference;
  return {
    size,
    center: size / 2,
    viewBox: `0 0 ${size} ${size}`,
    radius,
    stroke,
    // A strictly positive hole radius: the centre is empty, so the panel shows
    // through. A pie has no hole; this is the value the test asserts is > 0.
    holeRadius: radius - stroke / 2,
    circumference,
    isRing: true,
    pct: Math.round(clamped),
    circles: [
      { role: 'track', fill: 'none', stroke: 'var(--color-border-strong, var(--color-border-default))' },
      { role: 'progress', fill: 'none', stroke: 'var(--color-accent)', dashArray: `${dash} ${circumference - dash}` },
    ],
  };
}

/**
 * One fact per chip: when a tile's label leads with its own value token
 * (e.g. label "16 resolve dispatches", value "16"), strip the leading value so
 * the number is not rendered twice. Labels that do not repeat the value are
 * returned untouched. Purely presentational: nothing is derived or invented.
 *
 * @param {string} label
 * @param {string} value
 * @returns {{ value: string, label: string }}
 */
export function chipDisplay(label, value) {
  const v = String(value == null ? '' : value).trim();
  let l = String(label == null ? '' : label).trim();
  if (v) {
    if (l.toLowerCase() === v.toLowerCase()) {
      // the label IS the value: keep the figure, drop the redundant descriptor
      l = '';
    } else {
      // strip a leading "<value>" (optionally with punctuation) + separator
      const escaped = v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const stripped = l.replace(new RegExp('^' + escaped + '[\\s:.\\-]+', 'i'), '');
      if (stripped !== l) l = stripped;
    }
  }
  return { value: v, label: l };
}
